import Axios from "../../../utils/axiosInterceptor";
import { PATIENT_APIS } from "../../../utils/constants";
import { errorMessage } from "../../../utils/notificationUtils";

export const HEALTH_INDIA_PAYER_NAME = "Health India";

const HEALTH_INDIA_API_BASE = PATIENT_APIS.HEALTH_INDIA_SERVICE;

const dateFormat = "DD-MM-YYYY HH:mm:ss";

export type HealthIndiaPatientFetchDetails = {
  identityNumber: string;
  uhidNo?: string;
  hospitalId: number;
  schemeId: number;
  cardCategory?: string;
};

export type HealthIndiaPreAuthFormValues = {
  admissionDate?: string | null;
  dischargeDate?: string | null;
  stayInHospitalDays?: number | string | null;
  diagnosisAilment?: string;
  claimedAmount?: number | string | null;
  treatingDoctorName?: string;
  doctorRegistrationId?: string | null;
  doctorMobileNo?: string | null;
  admissionType?: string | null;
  claimId?: string | null;
  documentsList?: any[];
};

export type BuildHealthIndiaPreAuthPayloadParams = {
  schemeMetaDataId: number | string | undefined;
  portalMetaDataId?: number;
  casePreAuthId?: number;
  hospitalId?: number;
  schemeId?: number;
  beneficiaryId?: string;
  patientOpdIpdNumber?: string | number | undefined;
  integratedWithPortal?: boolean;
  action?: string;
  formValues: HealthIndiaPreAuthFormValues;
};

export type HealthIndiaApprovePayload = {
  caseId: number;
  visitId: number;
  stage: string;
  approved: boolean;
  casePreAuthId: number;
  portalMetaDataId?: number;
  remarks?: string;
  preAuthRequest: ReturnType<typeof buildHealthIndiaPreAuthPayload>;
};

const resolvePayerData = (response: any) => {
  const payers =
    response?.portals?.["health-india"]?.payers ||
    response?.portals?.healthIndia?.payers ||
    response?.portals?.ihx?.payers ||
    {};
  const matchedPayerKey = Object.keys(payers).find((payerKey) =>
    payerKey?.toUpperCase()?.includes(HEALTH_INDIA_PAYER_NAME.toUpperCase())
  );

  return matchedPayerKey ? payers?.[matchedPayerKey] : null;
};

const resolvePayerProviderData = (response: any) => {
  const payerProviders =
    response?.portals?.["health-india"]?.payerProviders ||
    response?.portals?.healthIndia?.payerProviders ||
    response?.portals?.ihx?.payerProviders ||
    {};
  const matchedPayerKey = Object.keys(payerProviders).find((payerKey) =>
    payerKey?.toUpperCase()?.includes(HEALTH_INDIA_PAYER_NAME.toUpperCase())
  );

  return matchedPayerKey ? payerProviders?.[matchedPayerKey] : null;
};

/** Maps Health India pre-auth API response for redux portalData (Ayushman-style). */
export const mapHealthIndiaPortalData = (response: any) => {
  if (!response || typeof response !== "object") {
    return null;
  }
  return {
    casePreAuthOthersResponse: response,
    casePreAuthHealthIndia: response,
  };
};

/**
 * Builds pre-auth request body aligned with HealthIndiaCasePreAuthRequestNew (same shape as CGHS/Ayushman others flow).
 */
export const buildHealthIndiaPreAuthPayload = ({
  schemeMetaDataId,
  portalMetaDataId,
  casePreAuthId = 0,
  hospitalId,
  schemeId,
  beneficiaryId,
  patientOpdIpdNumber,
  integratedWithPortal = true,
  action = "save",
  formValues,
}: BuildHealthIndiaPreAuthPayloadParams) => {
  const casePreAuthOthersRequest = {
    admissionDate: formValues?.admissionDate || null,
    dischargeDate: formValues?.dischargeDate || null,
    lengthOfStay:
      formValues?.stayInHospitalDays != null
        ? String(formValues.stayInHospitalDays)
        : null,
    requestedAmount:
      formValues?.claimedAmount != null ? String(formValues.claimedAmount) : null,
    diagnosis: formValues?.diagnosisAilment || null,
    doctorName: formValues?.treatingDoctorName || null,
    doctorRegistrationId: formValues?.doctorRegistrationId || null,
    doctorMobileNo: formValues?.doctorMobileNo || null,
    admissionType: formValues?.admissionType || "Planned",
    claimId: formValues?.claimId || null,
  };

  return {
    schemeMetaDataId: String(schemeMetaDataId),
    portalMetaDataId,
    casePreAuthId,
    casePreAuthRequest: {
      beneficiaryId,
      patientOpdIpdNumber,
    },
    casePreAuthOthersRequest,
    casePreAuthIhxRequest: null,
    casePreAuthCghsRequest: null,
    casePreAuthVidalRequest: null,
    casePreAuthStarhealthRequest: null,
    casePreAuthEchsRequest: null,
    hospitalId,
    schemeId,
    totalAmount:
      formValues?.claimedAmount != null ? String(formValues.claimedAmount) : "",
    integratedWithPortal,
    action,
    doa: formValues?.admissionDate || null,
    dod: formValues?.dischargeDate || null,
  };
};

export const buildHealthIndiaApprovePayload = (
  params: Omit<HealthIndiaApprovePayload, "preAuthRequest"> & {
    preAuthRequest: ReturnType<typeof buildHealthIndiaPreAuthPayload>;
  }
): HealthIndiaApprovePayload => ({
  caseId: params.caseId,
  visitId: params.visitId,
  stage: params.stage,
  approved: params.approved,
  casePreAuthId: params.casePreAuthId,
  portalMetaDataId: params.portalMetaDataId,
  remarks: params.remarks,
  preAuthRequest: params.preAuthRequest,
});

// ---------------------------------------------------------------------------
// Registration stage
// ---------------------------------------------------------------------------

export const fetchHealthIndiaBeneficiaryDetails = async (
  data: HealthIndiaPatientFetchDetails
) => {
  try {
    const response = await Axios.post(`${HEALTH_INDIA_API_BASE}/ben-fetch-details`, data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch Health India beneficiary details:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to fetch Health India beneficiary details"
    );
  }
};

// ---------------------------------------------------------------------------
// PreAuth stage
// ---------------------------------------------------------------------------

export const getAllCasePreAuthByCaseIdAndVisitId = async (
  caseId: number,
  visitId: number
) => {
  try {
    const response = await Axios.get(
      `${HEALTH_INDIA_API_BASE}/cases/${caseId}/visitInfo/${visitId}/getAllCasePreAuthByCaseIdAndVisitId`
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to get Health India pre-auth:", error?.response?.data);
    errorMessage(error?.response?.data?.data?.message || "Failed to get Health India pre-auth data. Please try again.");
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to get Health India pre-auth data"
    );
  }
};

/** Alias used by preAuth slice */
export const fetchPortalDataHealthIndia = getAllCasePreAuthByCaseIdAndVisitId;

export const saveDraftHealthIndia = async (
  caseId: number,
  visitId: number,
  formData: any
) => {
  try {
    const response = await Axios.post(
      `${HEALTH_INDIA_API_BASE}/cases/${caseId}/visitInfo/${visitId}/casePreAuth`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot save Health India pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot save Health India pre-auth details"
    );
  }
};

export const updateDraftHealthIndia = async (
  caseId: number,
  visitId: number,
  casePreAuthId: number,
  formData: any
) => {
  try {
    const response = await Axios.patch(
      `${HEALTH_INDIA_API_BASE}/cases/${caseId}/visitInfo/${visitId}/casePreAuthId/${casePreAuthId}`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Health India pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot update Health India pre-auth details"
    );
  }
};

export const intimateHealthIndiaPreAuth = async (
  caseId: number,
  visitId: number,
  formData: any
) => {
  return saveDraftHealthIndia(caseId, visitId, {
    ...formData,
    action: "intimate",
  });
};

/** Initiate pre-auth on Health India portal (CGHS-style: saves draft when casePreAuthId is 0). */
export const initiateCasePreAuthHealthIndia = async (
  caseId: number,
  visitId: number,
  payload: any
) => {
  try {
    const response = await Axios.post(
      `${HEALTH_INDIA_API_BASE}/cases/${caseId}/visitInfo/${visitId}/initiateCasePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to initiate Health India pre-auth on portal:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "Failed to initiate Health India pre-auth on portal"
    );
  }
};

export const approveHealthIndiaPreAuth = async (formData: HealthIndiaApprovePayload) => {
  try {
    const response = await Axios.post(`${HEALTH_INDIA_API_BASE}/approve`, formData);
    return response.data;
  } catch (error: any) {
    console.error("Failed to approve Health India pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to approve Health India pre-auth"
    );
  }
};

/** @deprecated use saveDraftHealthIndia */
export const postHealthIndiaPreAuth = saveDraftHealthIndia;

/** @deprecated use updateDraftHealthIndia */
export const updateHealthIndiaPreAuth = updateDraftHealthIndia;

/** @deprecated use getAllCasePreAuthByCaseIdAndVisitId */
export const getHealthIndiaPreAuth = getAllCasePreAuthByCaseIdAndVisitId;

// ---------------------------------------------------------------------------
// PreAuth stage – portal metadata helpers (shared IHX/CGHS adapter APIs)
// ---------------------------------------------------------------------------

export const getHealthIndiaDiagnosisOptions = async (
  portalMetaDataId: number,
  hospitalId: number,
  schemeId: number
) => {
  const response = await Axios.post(
    `${PATIENT_APIS.GET_DIAGNOSIS_DATA}/portalMetaDataId/${portalMetaDataId}/hospitalId/${hospitalId}/schemeId/${schemeId}`,
    { procedureCooperateMappingId: null }
  );

  const payerData = resolvePayerData(response.data);
  const diagnosisList = payerData?.diagnosisList || [];
  return diagnosisList.map((item: any) => ({
    value: item.id,
    label: item.diagnosis,
  }));
};

export const getHealthIndiaRoomTypeOptions = async (
  hospitalId: number,
  portalMetaDataId: number,
  schemeId: number
) => {
  const response = await Axios.get(
    `${PATIENT_APIS.GET_ROOMTYPES_DATA}/portalMetaDataId/${portalMetaDataId}/schemeId/${schemeId}/hospitalId/${hospitalId}`
  );

  const payerData = resolvePayerData(response.data);
  const roomTypes = payerData?.roomTypes || [];
  const generalRoomTypes = payerData?.generalRoomTypes || [];

  return {
    roomTypes: roomTypes.map((item: any) => ({
      value: item.id,
      label: item.providerRoomType,
    })),
    generalRoomTypes: generalRoomTypes.map((item: any) => ({
      value: item.id,
      label: item.roomType,
    })),
  };
};

export const getHealthIndiaProcedureOptions = async (
  hospitalId: number,
  portalMetaDataId: number,
  schemeId: number
) => {
  const response = await Axios.get(
    `${PATIENT_APIS.GET_PROCEDURE_DATA}/portalMetaDataId/${portalMetaDataId}/hospitalId/${hospitalId}/schemeId/${schemeId}`
  );

  const payerData = resolvePayerData(response.data);
  const procedureList = payerData?.procedureList || [];
  return procedureList.map((item: any) => ({
    value: item.id,
    label: item.procedure,
  }));
};

export const getHealthIndiaTreatmentOptions = async (portalMetaDataId: number) => {
  const response = await Axios.get(
    `${PATIENT_APIS.GET_TREATMENT_DATA}/portalMetaDataId/${portalMetaDataId}`
  );

  const payerProviderData = resolvePayerProviderData(response.data);
  return payerProviderData?.lineOfTreatments || [];
};

export const claimValidationHealthIndia = async (data: any) => {
  try {
    const response = await Axios.post(`${HEALTH_INDIA_API_BASE}/claim/validate`, data);
    return response.data;
  } catch (error: any) {
    console.error("Cannot validate Health India claim details:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot validate Health India claim details"
    );
  }
};

export const updateClaimValidationHealthIndia = async (
  caseId: number,
  visitId: number,
  data: any
) => {
  try {
    const response = await Axios.patch(
      `${HEALTH_INDIA_API_BASE}/claim/caseId/${caseId}/visitId/${visitId}/update`,
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Health India claim validation details:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot update Health India claim validation details"
    );
  }
};

export { dateFormat as HEALTH_INDIA_DATE_FORMAT };
